import { DEFAULT_UNIT, UNITS } from '../constants.js';
/**
 * Modo turbo: o usuario diz o que vai fazer ("bolo de cenoura para 5 pessoas")
 * e a IA confere contra o estoque de casa.
 *
 * A normalizacao mora aqui, em regra pura, pelo mesmo motivo da leitura por
 * foto (CLAUDE.md secao 8): a IA e enriquecimento, nunca requisito. Resposta
 * torta vira plano vazio — nunca erro na cara de quem perguntou — e nada do
 * que ela sugere entra na lista de compras sem o usuario mandar.
 */
/** `have` = tem em casa; `missing` = falta; `unsure` = a IA nao se decidiu. */
export const TURBO_STATUSES = ['have', 'missing', 'unsure'];
export const TURBO_VERDICTS = ['yes', 'partial', 'no'];
function text(value, max) {
    if (typeof value !== 'string')
        return null;
    const limpo = value.replace(/\s+/g, ' ').trim();
    if (!limpo)
        return null;
    const dobrado = limpo.toLowerCase();
    if (dobrado === 'null' || dobrado === 'n/a')
        return null;
    return limpo.slice(0, max);
}
function status(value) {
    const bruto = typeof value === 'string' ? value.toLowerCase().trim() : '';
    if (TURBO_STATUSES.includes(bruto))
        return bruto;
    // Sinonimos que a IA usa quando escapa do enum.
    if (['tem', 'sim', 'ok', 'in_stock', 'available'].includes(bruto))
        return 'have';
    if (['falta', 'nao', 'no', 'missing_item', 'unavailable'].includes(bruto))
        return 'missing';
    return 'unsure';
}
function verdict(value, ingredients) {
    const bruto = typeof value === 'string' ? value.toLowerCase().trim() : '';
    if (TURBO_VERDICTS.includes(bruto))
        return bruto;
    // Sem veredito utilizavel, os proprios ingredientes decidem.
    const faltando = ingredients.filter((linha) => linha.status !== 'have').length;
    if (faltando === 0)
        return 'yes';
    return faltando === ingredients.length ? 'no' : 'partial';
}
function unit(value) {
    const bruto = typeof value === 'string' ? value.trim() : '';
    return UNITS.includes(bruto) ? bruto : DEFAULT_UNIT;
}
function quantity(value) {
    const numero = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numero) || numero <= 0)
        return 1;
    return Math.min(1000, Math.round(numero * 100) / 100);
}
function lista(value) {
    return Array.isArray(value) ? value.slice(0, 60) : [];
}
/**
 * Normaliza o JSON da IA. Sem nenhum ingrediente utilizavel devolve `null`: a
 * tela mostra o recado e o usuario segue conferindo o estoque na mao.
 */
export function normalizeTurboPlan(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const campos = raw;
    const ingredients = [];
    for (const bruto of lista(campos['ingredients'])) {
        if (!bruto || typeof bruto !== 'object')
            continue;
        const linha = bruto;
        const name = text(linha['name'], 80);
        if (!name)
            continue;
        ingredients.push({
            name,
            needed: text(linha['needed'], 40),
            status: status(linha['status']),
            inStock: text(linha['inStock'], 120),
            note: text(linha['note'], 200),
        });
    }
    if (ingredients.length === 0)
        return null;
    const suggestions = [];
    for (const bruto of lista(campos['shopping'])) {
        if (!bruto || typeof bruto !== 'object')
            continue;
        const linha = bruto;
        const label = text(linha['label'], 120);
        if (!label)
            continue;
        // Sugerir o que ja tem em casa e o erro mais irritante: a lista de compras
        // encheria de coisa a toa. Na duvida entre os dois, vale o ingrediente.
        const correspondente = ingredients.find((item) => item.name.toLowerCase() === label.toLowerCase());
        if (correspondente?.status === 'have')
            continue;
        suggestions.push({
            label,
            quantity: quantity(linha['quantity']),
            unit: unit(linha['unit']),
            reason: text(linha['reason'], 200),
        });
    }
    return {
        understanding: text(campos['understanding'], 300) ?? '',
        verdict: verdict(campos['verdict'], ingredients),
        ingredients,
        suggestions,
        notes: text(campos['notes'], 500),
    };
}
/** Resumo em uma linha, para o topo da resposta. */
export function turboSummary(plan) {
    const faltando = plan.ingredients.filter((item) => item.status === 'missing').length;
    const duvidosos = plan.ingredients.filter((item) => item.status === 'unsure').length;
    if (plan.verdict === 'yes' && faltando === 0 && duvidosos === 0) {
        return 'Voce tem tudo o que precisa.';
    }
    const partes = [];
    if (faltando > 0)
        partes.push(`${faltando} ${faltando === 1 ? 'item falta' : 'itens faltam'}`);
    if (duvidosos > 0) {
        partes.push(`${duvidosos} ${duvidosos === 1 ? 'item para conferir' : 'itens para conferir'}`);
    }
    return partes.length > 0 ? partes.join(' e ') + '.' : 'Confira a lista abaixo.';
}
//# sourceMappingURL=turbo.js.map